import { systemColors } from "#ce/utils/color";
import { booleanAttribute } from "#ce/utils/props";

// The container roles are surface tints — around 1.1:1 against white on every surface — so a track
// filled with one is invisible. A track is a control, so it takes only the roles that carry a
// control-strength colour.
export const COLORS = systemColors.filter((color) => !color.endsWith("-container"));

// `checked` is absent on purpose and is documented in the story: the element owns a live `checked`
// property, which a declared prop would shadow with an accessor onto the prop store. The `checked`
// attribute names the state a reset restores, the way it does on a native checkbox.
export const props = Object.freeze({
  /**
   * Text rendered next to the switch. Clicking the label toggles the checked state.
   *
   * @type {String}
   * @default undefined
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * Text submitted under `name` while the switch is on. An off switch submits nothing, the way a
   * native checkbox behaves.
   *
   * @type {String}
   * @default "on"
   */
  value: {
    type: String,
    default: "on",
  },
  /**
   * Require the switch to be on before its form can be submitted. Presence-based, the way the
   * platform reads a boolean attribute: any value other than `false` constrains the field.
   *
   * @type {Boolean|String}
   * @default false
   */
  required: booleanAttribute(),
  /**
   * Message the browser shows when a required switch is submitted off. Names what this particular
   * switch is for; the generic wording comes from `frontend.forms`.
   *
   * @type {String}
   * @default undefined
   */
  requiredMessage: {
    type: String,
    default: undefined,
  },
  /**
   * Disable user interaction. Disabled switches keep their visual state but ignore clicks.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Guidance rendered below the switch while it has focus. `persistent-hint` keeps it visible.
   * A `messages` value takes its place.
   *
   * @type {String}
   * @default undefined
   */
  hint: {
    type: String,
    default: undefined,
  },
  /**
   * Keep the hint visible whether or not the switch has focus.
   *
   * @type {Boolean|String}
   * @default false
   */
  persistentHint: booleanAttribute(),
  /**
   * Message or messages rendered below the switch, in place of the hint. A single string, or
   * several as an array assigned through the `messages` property.
   *
   * @type {String|String[]}
   * @default undefined
   */
  messages: {
    type: [String, Array],
    default: undefined,
  },
  /**
   * System colour role the checked track takes.
   *
   * @type {String}
   * @default "primary"
   * @storybook-control select
   */
  color: {
    type: String,
    default: "primary",
    validator: (value) => COLORS.includes(value),
  },
  /**
   * Render the label before the switch instead of after.
   *
   * @type {Boolean|String}
   * @default false
   */
  labelPrepend: booleanAttribute(),
});
